import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Circle, Lock } from 'lucide-react';

export type LessonCardStatus = 'done' | 'current' | 'upcoming' | 'locked';

interface Props {
  to: string;
  title: string;
  objective: string;
  duration?: number;
  skill?: string;
  status?: LessonCardStatus;
  ctaLabel?: string;
  className?: string;
}

const ICONS = {
  done: CheckCircle2,
  current: Circle,
  upcoming: Circle,
  locked: Lock,
};

export default function LessonCard({
  to,
  title,
  objective,
  duration,
  skill,
  status = 'upcoming',
  ctaLabel,
  className = '',
}: Props) {
  const Icon = ICONS[status];
  const muted = status === 'locked' || status === 'upcoming';
  const ringClass =
    status === 'current'
      ? 'border-ink-900'
      : status === 'done'
      ? 'border-ink-200'
      : 'border-ink-100';

  const inner = (
    <div
      className={`bg-white border ${ringClass} rounded-2xl p-5 sm:p-6 transition group ${
        muted ? 'opacity-90' : ''
      } ${status !== 'locked' ? 'hover:border-ink-400' : ''} ${className}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <Icon
          className={`w-4 h-4 mt-1 flex-shrink-0 ${
            status === 'done' ? 'text-ink-900' : status === 'locked' ? 'text-ink-300' : 'text-ink-400'
          }`}
        />
        <div className="flex-1 min-w-0">
          {skill && (
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-1">
              Skill · {skill}
            </div>
          )}
          <div className="font-display text-xl leading-snug">{title}</div>
        </div>
      </div>
      <p className="text-sm text-ink-600 leading-relaxed mb-4">{objective}</p>
      <div className="flex items-center justify-between text-xs text-ink-500">
        <span>{duration ? `${duration} min` : ''}</span>
        <span className="inline-flex items-center gap-1 text-ink-700 group-hover:gap-2 transition-all">
          {ctaLabel ?? (status === 'done' ? 'Review' : status === 'current' ? 'Continue' : 'Open')}
          <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </div>
  );

  if (status === 'locked') {
    return <div className={`cursor-not-allowed ${className}`}>{inner}</div>;
  }
  return <Link to={to}>{inner}</Link>;
}
